import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { colorForShift, shiftLabel } from "@/lib/shifts";
import { format, parseISO } from "date-fns";
import { mdyDow, mdyTime } from "@/lib/dateFmt";
import { Trash2, Plus, History as HistoryIcon, StickyNote, Users, User, Eye, Clock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const VIS_META = {
  managers: { label: "Managers only", Icon: Eye,   badge: "bg-slate-200 text-slate-700" },
  employee: { label: "This employee", Icon: User,  badge: "bg-blue-100 text-blue-800"   },
  all:      { label: "Everyone",      Icon: Users, badge: "bg-emerald-100 text-emerald-800" },
};

export default function ShiftDialog({ open, onClose, employee, date, current, codes, facilityId, dayAssignments = [], onSaved }) {
  const [selected, setSelected]   = useState(current?.shift_code || null);
  const [notes, setNotes]         = useState([]);
  const [history, setHistory]     = useState([]);
  const [noteText, setNoteText]   = useState("");
  const [noteVis, setNoteVis]     = useState("managers");
  const [tab, setTab]             = useState("assign");
  const [saving, setSaving]       = useState(false);
  const [modifyHours, setModifyHours] = useState(false);
  const [startOverride, setStartOverride] = useState("");
  const [endOverride, setEndOverride] = useState("");
  const [modifyNote, setModifyNote] = useState("");
  const [loaEnd, setLoaEnd] = useState("");
  const [absentOpen, setAbsentOpen] = useState(false);
  const [absentType, setAbsentType] = useState("");
  const [absentNote, setAbsentNote] = useState("");
  const [absentLeftAt, setAbsentLeftAt] = useState("");
  const [absentTypes, setAbsentTypes] = useState([]);
  // Custom-hours input for adjustable-hours codes. Tr inherits from its
  // shadowed shift; Cvg / Oth take a manager-picked start/end so the manager
  // says "I need someone from 11:00 to 15:00" rather than "give them 4 hours".
  const [customHours, setCustomHours] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  // Training pair pick: when Tr is picked, choose a shift-code to shadow +
  // the specific employee already on that shift on this date.
  const [trainingOfCode, setTrainingOfCode] = useState("");
  const [trainerId, setTrainerId] = useState("");
  // Keep-both stacking state — when a manager picks a different code on a cell
  // that already has one, we offer Override vs. Keep-both. If Keep-both, we
  // reveal a 2-row editor and submit via /schedule/stack.
  const [stackMode, setStackMode] = useState(false);
  const [primaryEdit, setPrimaryEdit] = useState({ start: "", end: "", hours: "", ot: "" });
  const [secondEdit,  setSecondEdit ] = useState({ start: "", end: "", hours: "", ot: "" });
  // Admin-set department order (drives the grouping order of work shifts).
  const [deptOrder, setDeptOrder] = useState([]);

  useEffect(() => {
    if (!open || !facilityId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/facilities/${facilityId}/settings`);
        if (!cancelled) setDeptOrder(Array.isArray(data?.department_order) ? data.department_order : []);
      } catch { /* not fatal */ }
    })();
    return () => { cancelled = true; };
  }, [open, facilityId]);

  const key = employee && date ? `${employee.id}|${date}` : null;

  useEffect(() => {
    if (!open || !employee || !date) return;
    setSelected(current?.shift_code || null);
    setNoteText("");
    setNoteVis("managers");
    setTab("assign");
    const wasMod = !!(current?.is_modified || current?.hours_override != null);
    setModifyHours(wasMod);
    setStartOverride(current?.start_time || "");
    setEndOverride(current?.end_time || "");
    setModifyNote(current?.modified_note || "");
    setLoaEnd("");
    setAbsentOpen(false);
    setAbsentType("");
    setAbsentNote("");
    setAbsentLeftAt("");
    setCustomHours(current?.hours_override != null ? String(current.hours_override) : "");
    // Cvg / Oth prefill from saved start/end (an existing custom slot). Fresh
    // assignments leave these blank so the manager has to explicitly pick.
    setCustomStart(current?.start_time || "");
    setCustomEnd(current?.end_time || "");
    setStackMode(false);
    setPrimaryEdit({ start: "", end: "", hours: "", ot: "" });
    setSecondEdit({ start: "", end: "", hours: "", ot: "" });
    setTrainingOfCode(current?.training_of_code || "");
    setTrainerId(current?.trainer_employee_id || "");
    if (absentTypes.length === 0) {
      api.get(`/absent-types`).then((r) => setAbsentTypes(r.data || [])).catch(() => {});
    }
    (async () => {
      try {
        const [n, h] = await Promise.all([
          api.get(`/shift-notes?employee_id=${employee.id}&date=${date}`),
          api.get(`/history?employee_id=${employee.id}&date=${date}`),
        ]);
        setNotes(n.data || []);
        setHistory(h.data || []);
      } catch { /* ignore */ }
    })();
  }, [open, key, current]);

  const reloadCellData = async () => {
    if (!employee || !date) return;
    const [n, h] = await Promise.all([
      api.get(`/shift-notes?employee_id=${employee.id}&date=${date}`),
      api.get(`/history?employee_id=${employee.id}&date=${date}`),
    ]);
    setNotes(n.data || []);
    setHistory(h.data || []);
  };

  const submitAbsent = async () => {
    if (!absentType) { toast.error("Pick an absent type first"); return; }
    if (absentType === "left_sick_early" && !absentLeftAt) {
      toast.error("Enter the time the employee left"); return;
    }
    setSaving(true);
    try {
      await api.post(`/schedule/mark-absent`, {
        facility_id: facilityId, employee_id: employee.id,
        date, absent_type: absentType, note: absentNote.trim() || null,
        left_at: absentType === "left_sick_early" ? absentLeftAt : null,
      });
      toast.success(`Marked absent — ${absentTypes.find(t => t.code === absentType)?.label || absentType}`);
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark absent"); }
    setSaving(false);
  };

  const saveShift = async (code) => {
    setSaving(true);
    try {
      // Manual "P" (PTO Pending) from a manager/admin → file a real PTO
      // request on behalf of the employee so it goes through the normal
      // review workflow, keeps the underlying shift in `prev_shift_code`,
      // and lands on the Requests tab as Pending (magenta cell).
      if (code === "P") {
        await api.post("/pto-requests", {
          employee_id: employee.id,
          dates: [date],
          reason: "Filed by manager from schedule grid",
        });
        toast.success("PTO request filed — pending review");
        onSaved && onSaved();
        onClose();
        setSaving(false);
        return;
      }
      // Special LOA range flow — apply code L across a start/end range, and any
      // prior shifts get displaced onto the open shifts strip automatically.
      if (code === "L" && loaEnd && loaEnd >= date) {
        const { data: resp } = await api.post("/schedule/bulk-loa", {
          employee_id: employee.id,
          facility_id: facilityId,
          start_date: date,
          end_date: loaEnd,
          code: "L",
        });
        toast.success(`LOA applied for ${resp.days_updated} day${resp.days_updated === 1 ? "" : "s"}`);
        if (resp.displaced?.length) {
          toast.message(`${resp.displaced.length} shift${resp.displaced.length === 1 ? "" : "s"} moved to open line`);
        }
        onSaved && onSaved();
        onClose();
        setSaving(false);
        return;
      }
      const payload = {
        employee_id: employee.id, date, shift_code: code, facility_id: facilityId,
      };
      const sh = codes.find(c => c.code === code);
      // Adjustable-hours utility codes (Tr / Cvg / Oth): the manager types the
      // hours per assignment. Send them as `hours_override` so the assignment
      // records the actual worked amount instead of the code's default.
      // Training pair: when Tr is picked, we require the shadowed shift code
      // and (usually) a trainer. Both are sent so the backend can save the
      // link and drop a "Training X" note on the trainer's cell.
      if (code === "Tr") {
        if (!trainingOfCode) {
          toast.error("Pick which shift to shadow");
          setSaving(false); return;
        }
        payload.training_of_code = trainingOfCode;
        payload.trainer_employee_id = trainerId || null;
      }
      if (code && code !== "Tr" && sh?.adjustable_hours) {
        // Cvg / Oth: manager picks a start/end for the extra shift. Send it
        // as start_override + end_override so the backend records the exact
        // worked window (and effective_hours falls out from the difference).
        if (!customStart || !customEnd) {
          toast.error(`Pick start & end for ${code}`);
          setSaving(false); return;
        }
        payload.start_override = customStart;
        payload.end_override = customEnd;
      }
      if (code && modifyHours && sh) {
        const defS = sh.start || null;
        const defE = sh.end || null;
        if (startOverride && startOverride !== defS) payload.start_override = startOverride;
        if (endOverride && endOverride !== defE) payload.end_override = endOverride;
        payload.modified_note = modifyNote || null;
      }
      const { data: resp } = await api.post("/schedule/assign", payload);
      const mod = payload.start_override || payload.end_override;
      toast.success(code ? (mod ? `Assigned ${code} · ${startOverride || sh?.start}–${endOverride || sh?.end}` : `Assigned ${code}`) : "Cleared shift");
      const warnings = resp?.ot_warnings || [];
      warnings.forEach((w) => toast.warning("OT rule: " + w, { duration: 8000 }));
      // Await parent refetch BEFORE closing so the grid never shows a stale
      // cell after the dialog dismisses — the previous behaviour was making
      // users click "Clear day" twice because the first click closed the
      // dialog before the schedule had reloaded.
      if (onSaved) await onSaved();
      onClose();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  // Submit a "Keep both" stack via /schedule/stack. Uses the two-row editor
  // state; ot_hours are per-shift so payroll can pull them later.
  const saveStack = async () => {
    if (!selected || !current?.shift_code) return;
    setSaving(true);
    try {
      const pShift = codes.find((c) => c.code === current.shift_code) || {};
      const sShift = codes.find((c) => c.code === selected) || {};
      const build = (edit, def) => ({
        shift_code: def.code,
        start_time: edit.start || def.start || null,
        end_time:   edit.end   || def.end   || null,
        hours:      edit.hours ? parseFloat(edit.hours) : null,
        ot_hours:   edit.ot    ? parseFloat(edit.ot)    : 0,
      });
      const payload = {
        facility_id: facilityId,
        employee_id: employee.id,
        date,
        primary: build(primaryEdit, pShift),
        extras:  [build(secondEdit, sShift)],
      };
      const { data: resp } = await api.post("/schedule/stack", payload);
      toast.success(`Stacked ${current.shift_code} + ${selected}`);
      (resp?.ot_warnings || []).forEach((w) => toast.warning("OT rule: " + w, { duration: 8000 }));
      if (onSaved) await onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to stack"); }
    setSaving(false);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post("/shift-notes", {
        employee_id: employee.id, date, content: noteText.trim(), visibility: noteVis,
      });
      setNoteText("");
      await reloadCellData();
      onSaved && onSaved();
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/shift-notes/${id}`);
      await reloadCellData();
      onSaved && onSaved();
      toast.success("Note deleted");
    } catch { toast.error("Failed"); }
  };

  const workCodes    = codes.filter((c) => c.type === "work");
  const specialCodes = codes.filter((c) => c.type !== "work" && c.code !== "A");

  // Group work shifts by department. Codes without a department fall under
  // "Unassigned" so they still render. Departments render in a deterministic
  // order (by name); the "Unassigned" bucket always renders last.
  const workByDept = React.useMemo(() => {
    const buckets = new Map();
    workCodes.forEach((s) => {
      const key = (s.department || "").trim() || "__unassigned__";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(s);
    });
    const entries = Array.from(buckets.entries());
    const orderIdx = new Map(deptOrder.map((n, i) => [n, i]));
    entries.sort(([a], [b]) => {
      if (a === "__unassigned__") return 1;
      if (b === "__unassigned__") return -1;
      const ia = orderIdx.has(a) ? orderIdx.get(a) : Number.POSITIVE_INFINITY;
      const ib = orderIdx.has(b) ? orderIdx.get(b) : Number.POSITIVE_INFINITY;
      if (ia !== ib) return ia - ib;
      return a.localeCompare(b);
    });
    return entries.map(([key, list]) => ({
      key,
      label: key === "__unassigned__" ? "Unassigned" : key,
      color: (list.find((s) => s.department_color)?.department_color) || null,
      codes: list.slice().sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true })),
    }));
  }, [workCodes, deptOrder]);

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto thin-scroll" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {employee?.name} · {date && mdyDow(date)}
            </DialogTitle>
            <DialogDescription>
              Assign a shift, manage notes for this cell, and view the change history.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="assign" data-testid="tab-assign">Assign</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">
                Notes{notes.length > 0 && <span className="ml-1 rounded-full bg-amber-500 text-white text-[10px] px-1.5">{notes.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <HistoryIcon size={12} className="mr-1" /> History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="assign" className="space-y-3 pt-3">
              {workCodes.length > 0 && (
                <div className="space-y-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Work shifts</div>
                  {workByDept.map((group) => (
                    <div key={group.key} className="rounded-lg border border-slate-200 bg-slate-50/60 overflow-hidden" data-testid={`dept-group-${group.key}`}>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border-b border-slate-200">
                        {group.color && (
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} aria-hidden />
                        )}
                        <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-600">{group.label}</span>
                        <span className="text-[10px] text-slate-400 tabular-nums ml-auto">{group.codes.length}</span>
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 p-2">
                        {group.codes.map((s) => {
                          const c = colorForShift(s);
                          const active = selected === s.code;
                          return (
                            <Tooltip key={s.code}>
                              <TooltipTrigger asChild>
                                <button
                                  data-testid={`shift-option-${s.code}`}
                                  onClick={() => setSelected(s.code)}
                                  className={`rounded-lg border p-2 text-center transition-colors
                                              ${c.bg} ${c.text} ${c.border}
                                              ${active ? "ring-2 ring-blue-500 ring-offset-1" : "hover:border-blue-400"}`}
                                >
                                  <div className="font-display font-bold text-lg">{s.code}</div>
                                  <div className="text-[10px] opacity-80 tabular-nums">
                                    {s.start && s.end ? `${s.start}–${s.end}` : `${s.hours}h`}
                                  </div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">{shiftLabel(s)}</TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Time off & special</div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {specialCodes.map((s) => {
                    const c = colorForShift(s);
                    const active = selected === s.code;
                    return (
                      <button
                        key={s.code}
                        data-testid={`shift-option-${s.code}`}
                        onClick={() => setSelected(s.code)}
                        className={`rounded-lg border p-2 text-center text-sm transition-colors
                                    ${c.bg} ${c.text} ${c.border}
                                    ${active ? "ring-2 ring-blue-500 ring-offset-1" : "hover:border-blue-400"}`}
                      >
                        <div className="font-semibold">{s.code}</div>
                        <div className="text-[10px] opacity-90">{s.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selected === "B" && (
                <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid="bereavement-info-box">
                  <div className="text-sm font-semibold text-sky-900 flex items-center gap-1.5">
                    Bereavement leave · policy summary
                  </div>
                  <div className="text-[11px] text-sky-900/90 leading-relaxed">
                    Compensation replaces regularly scheduled workdays missed. These hours <b>do not count toward overtime</b>. Reproductive-loss leave must be taken within 3 months and is capped at 20 days in a 12-month period.
                  </div>
                  <div className="overflow-hidden rounded-md border border-sky-200 bg-white">
                    <table className="w-full text-[11px]" data-testid="bereavement-policy-table">
                      <thead className="bg-sky-100 text-sky-900 uppercase tracking-widest text-[10px]">
                        <tr>
                          <th className="text-left px-2 py-1.5">Relation</th>
                          <th className="text-center px-2 py-1.5">Regular / FT</th>
                          <th className="text-center px-2 py-1.5">Per-Diem / Temp</th>
                          <th className="text-center px-2 py-1.5">Pay code</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-800">
                        <tr className="border-t border-sky-100">
                          <td className="px-2 py-1.5 align-top">
                            <div className="font-semibold">Family Member</div>
                            <div className="text-[10px] text-slate-500 leading-snug mt-0.5">
                              Child · parent · spouse · registered domestic partner · grandparent · grandchild · sibling · parent-in-law · son/daughter-in-law. Step-relationships = blood; great-grand = grand.
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            <div className="font-bold">3 days paid</div>
                            <div className="text-[10px] text-slate-500">+ 2 days unpaid or PTO</div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            <div className="font-bold">5 days unpaid</div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top tabular-nums font-mono">
                            <div>FT: 700</div>
                            <div>PD: 912</div>
                          </td>
                        </tr>
                        <tr className="border-t border-sky-100">
                          <td className="px-2 py-1.5 align-top">
                            <div className="font-semibold">Relative</div>
                            <div className="text-[10px] text-slate-500 leading-snug mt-0.5">
                              Aunt · uncle · niece · nephew · cousin · grandparent-in-law · sibling-in-law. <i>Must live in the employee's home.</i>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            <div className="font-bold">1 day paid</div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            <div className="font-bold">—</div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top tabular-nums font-mono">
                            700
                          </td>
                        </tr>
                        <tr className="border-t border-sky-100">
                          <td className="px-2 py-1.5 align-top">
                            <div className="font-semibold">Reproductive Loss Event</div>
                            <div className="text-[10px] text-slate-500 leading-snug mt-0.5">
                              Within 3 months of the event; up to 20 days in any 12-month window. Employee may swap in PTO or sick leave.
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            <div className="font-bold">5 days unpaid</div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            <div className="font-bold">5 days unpaid</div>
                          </td>
                          <td className="px-2 py-1.5 text-center align-top tabular-nums font-mono">
                            912
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selected === "L" && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2" data-testid="loa-range-box">
                  <div className="text-sm font-medium text-violet-900 flex items-center gap-1.5">
                    Leave of absence range
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-xs shrink-0">Start</Label>
                    <input
                      type="date"
                      value={date || ""}
                      readOnly
                      className="rounded-md border border-slate-300 bg-slate-50 px-2 py-1 text-sm text-slate-600 tabular-nums"
                      data-testid="loa-start-input"
                    />
                    <span className="text-xs text-slate-500">→</span>
                    <Label className="text-xs shrink-0">End</Label>
                    <input
                      type="date"
                      min={date || undefined}
                      value={loaEnd}
                      onChange={(e) => setLoaEnd(e.target.value)}
                      data-testid="loa-end-input"
                      className="rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums"
                    />
                    {loaEnd && loaEnd >= date && (
                      <span className="text-xs text-violet-800 font-semibold tabular-nums">
                        {(Math.floor((new Date(loaEnd) - new Date(date)) / 86400000) + 1)} days
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-violet-800/80">
                    Every date from start to end will be marked <b>L</b>. Any shifts already scheduled in that range get moved to the open shifts line so you can reassign coverage.
                  </div>
                </div>
              )}
              {/* Keep-both stacking prompt — appears when the manager picks a
                 different code on a cell that already has one. Explicit choice
                 between overriding the existing shift or stacking both. */}
              {selected && current?.shift_code && selected !== current.shift_code
                && codes.find(c => c.code === selected)?.type === "work"
                && codes.find(c => c.code === current.shift_code)?.type === "work"
                && !stackMode && (
                <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-2" data-testid="stack-prompt">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-900">
                      {employee?.name} already has <span className="font-mono">{current.shift_code}</span> on this day
                    </div>
                    <div className="text-[11px] text-amber-800/80 mt-0.5">
                      Override the existing shift, or keep both and split hours across the two.
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm" variant="outline"
                        className="border-amber-400 text-amber-900 hover:bg-amber-100"
                        onClick={() => {
                          const p = codes.find(c => c.code === current.shift_code) || {};
                          const s = codes.find(c => c.code === selected) || {};
                          // Prefer the ACTUAL assigned times/hours on the cell
                          // (which may have been custom-edited) over the shift
                          // code's defaults so the cap math is accurate.
                          const pStart = current?.start_time || p.start || "";
                          const pEnd   = current?.end_time   || p.end   || "";
                          const pHours = Number(current?.hours ?? p.hours ?? 0);
                          // Chain second shift to primary's end and cap the
                          // combined day at 12h — measured BOTH by paid hours
                          // and by clock-time from the primary's start (so
                          // "start 6am → end 6pm max" holds even if paid hours
                          // don't add up to 12 due to lunch deductions).
                          const addHours = (t, h) => {
                            if (!t || h <= 0) return t;
                            const [hh, mm] = t.split(":").map(Number);
                            let tot = hh * 60 + mm + Math.round(h * 60);
                            tot = ((tot % 1440) + 1440) % 1440;
                            return `${String(Math.floor(tot / 60)).padStart(2, "0")}:${String(tot % 60).padStart(2, "0")}`;
                          };
                          const rawSpan = (a, b) => {
                            if (!a || !b) return 0;
                            const [ah, am] = a.split(":").map(Number);
                            const [bh, bm] = b.split(":").map(Number);
                            let m = (bh * 60 + bm) - (ah * 60 + am);
                            if (m < 0) m += 1440;
                            return Math.round((m / 60) * 100) / 100;
                          };
                          const sStart = pEnd || s.start || "";
                          const remaining = Math.max(0, 12 - pHours);
                          const origEnd = s.end || "";
                          const spanToOrig = origEnd ? rawSpan(sStart, origEnd) : 0;
                          let sEnd;
                          if (origEnd && spanToOrig > 0 && spanToOrig <= remaining) {
                            sEnd = origEnd;
                          } else {
                            sEnd = addHours(sStart, remaining) || origEnd;
                          }
                          // Clock-time cap: never let end exceed primary_start + 12h.
                          if (pStart && sEnd) {
                            const spanFromPrimary = rawSpan(pStart, sEnd);
                            if (spanFromPrimary > 12) {
                              sEnd = addHours(pStart, 12) || sEnd;
                            }
                          }
                          const sHours = rawSpan(sStart, sEnd);
                          setPrimaryEdit({ start: pStart, end: pEnd, hours: String(pHours), ot: "" });
                          setSecondEdit({
                            start: sStart, end: sEnd,
                            hours: sHours ? sHours.toFixed(2) : "",
                            ot: sHours ? sHours.toFixed(2) : "",
                          });
                          setStackMode(true);
                        }}
                        data-testid="stack-keep-both"
                      >
                        Keep both
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="text-slate-600 hover:bg-slate-100"
                        onClick={() => setStackMode(false)}
                        data-testid="stack-override"
                      >
                        Override (default)
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {stackMode && (
                <div className="rounded-lg border-2 border-blue-400 bg-blue-50/50 p-3 space-y-3" data-testid="stack-editor">
                  <div className="text-sm font-semibold text-blue-900 flex items-center gap-1.5">
                    <Clock size={14} /> Stacked shifts · adjust start / end / hours / OT for each
                  </div>
                  {[
                    { key: "primary", code: current?.shift_code, edit: primaryEdit, setEdit: setPrimaryEdit },
                    { key: "second",  code: selected,            edit: secondEdit,  setEdit: setSecondEdit  },
                  ].map((row) => (
                    <div key={row.key} className="rounded-md border border-slate-200 bg-white p-2.5" data-testid={`stack-row-${row.key}`}>
                      <div className="text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-2">
                        <span className="font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300">{row.code}</span>
                        {codes.find(c => c.code === row.code)?.label}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Start</Label>
                          <Input type="time" value={row.edit.start} onChange={(e) => row.setEdit({ ...row.edit, start: e.target.value })}
                            className="tabular-nums text-sm" data-testid={`stack-${row.key}-start`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">End</Label>
                          <Input type="time" value={row.edit.end} onChange={(e) => row.setEdit({ ...row.edit, end: e.target.value })}
                            className="tabular-nums text-sm" data-testid={`stack-${row.key}-end`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Hours</Label>
                          <Input type="number" step="0.25" min="0" value={row.edit.hours}
                            onChange={(e) => row.setEdit({ ...row.edit, hours: e.target.value })}
                            className="tabular-nums text-sm" data-testid={`stack-${row.key}-hours`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-red-600">OT hours</Label>
                          <Input type="number" step="0.25" min="0" value={row.edit.ot}
                            onChange={(e) => row.setEdit({ ...row.edit, ot: e.target.value })}
                            className="tabular-nums text-sm border-red-300" placeholder="0"
                            data-testid={`stack-${row.key}-ot`} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-slate-500">
                    Cell will show <span className="font-mono">{current?.shift_code}, {selected}</span>.
                    Total hours = {(parseFloat(primaryEdit.hours || 0) + parseFloat(secondEdit.hours || 0)).toFixed(2)}h ·
                    OT = {(parseFloat(primaryEdit.ot || 0) + parseFloat(secondEdit.ot || 0)).toFixed(2)}h.
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" size="sm" onClick={() => setStackMode(false)}>Cancel</Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={saving}
                      onClick={saveStack} data-testid="stack-save">
                      {saving ? "Saving…" : "Save stacked shifts"}
                    </Button>
                  </div>
                </div>
              )}
              {selected === "Tr" && (() => {
                // Build the list of work-shift codes with the employee (if any)
                // currently working that code on this date. Manager clicks
                // a code → picks the trainer at the same time.
                const workCodes = codes.filter((c) => c.type === "work" && c.code !== "Tr");
                return (
                  <div className="rounded-lg border-2 border-orange-300 bg-orange-50/60 p-3 space-y-2" data-testid="training-picker">
                    <div className="text-sm font-semibold text-orange-900 flex items-center gap-1.5">
                      <Clock size={14} /> Which shift is {employee?.name?.split(" ")?.[0] || "the trainee"} shadowing?
                    </div>
                    <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                      {workCodes.map((c) => {
                        const currentAssignees = (dayAssignments || []).filter(
                          (a) => a.shift_code === c.code && !a.external && a.employee_id !== employee?.id
                        );
                        const hasAssignee = currentAssignees.length > 0;
                        const active = trainingOfCode === c.code;
                        return (
                          <div
                            key={c.code}
                            className={`rounded-md border overflow-hidden ${active ? "border-orange-500 ring-2 ring-orange-200" : "border-slate-200 bg-white"}`}
                            data-testid={`training-of-${c.code}`}
                          >
                            <div className="px-2 py-1.5 text-xs font-bold text-slate-800 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                              <span className="font-mono px-1.5 py-0.5 rounded bg-white border border-slate-300">{c.code}</span>
                              <span className="text-slate-500 font-medium">{c.label}</span>
                              {c.start && c.end && <span className="text-slate-400 tabular-nums text-[10px]">{c.start}–{c.end}</span>}
                            </div>
                            <div className="p-1.5 space-y-1">
                              {hasAssignee ? currentAssignees.map((asn) => {
                                const chosen = trainingOfCode === c.code && trainerId === asn.employee_id;
                                return (
                                  <button
                                    key={asn.employee_id}
                                    onClick={() => { setTrainingOfCode(c.code); setTrainerId(asn.employee_id); }}
                                    className={`w-full text-left rounded px-2 py-1 text-xs transition-colors ${
                                      chosen
                                        ? "bg-orange-500 text-white font-semibold"
                                        : "bg-white hover:bg-orange-100 text-slate-700 border border-slate-200"
                                    }`}
                                    data-testid={`training-trainer-${c.code}-${asn.employee_id}`}
                                  >
                                    {asn.employee_name || "Employee"}
                                    {chosen && <span className="ml-1 text-[10px] uppercase tracking-widest">← Trainer</span>}
                                  </button>
                                );
                              }) : (
                                <button
                                  onClick={() => { setTrainingOfCode(c.code); setTrainerId(""); }}
                                  className={`w-full text-left rounded px-2 py-1 text-xs italic transition-colors ${
                                    active && !trainerId
                                      ? "bg-orange-500 text-white font-semibold"
                                      : "bg-white hover:bg-orange-100 text-slate-500 border border-dashed border-slate-300"
                                  }`}
                                  data-testid={`training-no-trainer-${c.code}`}
                                >
                                  Nobody scheduled — shadow the shift only
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-orange-800/70">
                      Cell will show <span className="font-mono">{trainingOfCode || "?"}Tr</span> and stay orange. The trainer&apos;s cell gets a small orange <span className="font-mono">Tr</span> badge with a &ldquo;Training {employee?.name?.split(" ")?.[0]}&rdquo; note.
                    </div>
                  </div>
                );
              })()}
              {selected && selected !== "Tr" && codes.find(c => c.code === selected)?.adjustable_hours && (() => {
                const sh = codes.find(c => c.code === selected);
                const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
                let previewH = 0;
                if (customStart && customEnd) {
                  let d = toMin(customEnd) - toMin(customStart);
                  if (d < 0) d += 24 * 60;
                  previewH = Math.round((d / 60) * 100) / 100;
                  if (previewH > 6) previewH = Math.round((previewH - 0.5) * 100) / 100;
                }
                return (
                  <div className="rounded-lg border-2 border-sky-300 bg-sky-50/50 p-3 space-y-2" data-testid="adjustable-hours-box">
                    <div className="text-sm font-medium text-sky-900 flex items-center gap-1.5">
                      <Clock size={14} /> Pick start & end for {selected} · {sh?.label}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Start</Label>
                        <Input
                          type="time"
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                          className="tabular-nums text-sm"
                          data-testid="adjustable-start-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">End</Label>
                        <Input
                          type="time"
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          className="tabular-nums text-sm"
                          data-testid="adjustable-end-input"
                        />
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Extra shift — counts as <span className="font-semibold tabular-nums">{previewH || "?"}h</span> worked. Does not affect the open shifts line.
                    </div>
                  </div>
                );
              })()}
              {selected && !codes.find(c => c.code === selected)?.adjustable_hours && codes.find(c => c.code === selected)?.start && codes.find(c => c.code === selected)?.end && (() => {
                const sh = codes.find(c => c.code === selected);
                const defS = sh?.start || "";
                const defE = sh?.end || "";
                // Compute effective hours preview
                const effStart = startOverride || defS;
                const effEnd = endOverride || defE;
                const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
                let previewH = sh?.hours || 0;
                if (effStart && effEnd) {
                  let d = toMin(effEnd) - toMin(effStart);
                  if (d < 0) d += 24 * 60;
                  previewH = Math.round((d / 60) * 100) / 100;
                }
                const changed = (effStart !== defS) || (effEnd !== defE);
                return (
                  <div className="rounded-lg border border-pink-200 bg-pink-50/40 p-3 space-y-2" data-testid="modify-hours-box">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={modifyHours}
                        onChange={(e) => {
                          setModifyHours(e.target.checked);
                          if (!e.target.checked) { setStartOverride(""); setEndOverride(""); setModifyNote(""); }
                          else { setStartOverride(defS); setEndOverride(defE); }
                        }}
                        data-testid="modify-hours-toggle"
                        className="w-4 h-4 accent-pink-600"
                      />
                      <span className="font-medium text-pink-900">Modify hours (partial shift)</span>
                    </label>
                    {modifyHours && (
                      <div className="space-y-2 pl-6">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label className="text-xs shrink-0">Start</Label>
                          <input
                            type="time"
                            value={startOverride}
                            onChange={(e) => setStartOverride(e.target.value)}
                            data-testid="modify-start-input"
                            className="rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums"
                          />
                          <span className="text-slate-500 text-xs">→</span>
                          <Label className="text-xs shrink-0">End</Label>
                          <input
                            type="time"
                            value={endOverride}
                            onChange={(e) => setEndOverride(e.target.value)}
                            data-testid="modify-end-input"
                            className="rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums"
                          />
                          <span className={`text-xs tabular-nums ${changed ? "text-pink-800 font-semibold" : "text-slate-500"}`}>
                            = {previewH}h
                            {changed && sh?.hours ? <span className="text-slate-500 font-normal"> (was {sh.hours}h)</span> : null}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Default: {defS || "—"}–{defE || "—"}. Coverage gap will appear as {selected}Cvg in the open shifts strip — click it to assign a coverer.
                        </div>
                        <input
                          type="text"
                          value={modifyNote}
                          onChange={(e) => setModifyNote(e.target.value)}
                          placeholder="Reason (late start, long lunch, leaving early…)"
                          data-testid="modify-hours-note"
                          className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex justify-between pt-2">
                <div className="flex gap-2">
                  <Button
                    variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => saveShift(null)}
                    data-testid="shift-clear"
                  >
                    <Trash2 size={14} className="mr-1" /> Clear day
                  </Button>
                  <Button
                    variant="ghost" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                    onClick={() => setAbsentOpen((o) => !o)}
                    data-testid="shift-mark-absent-toggle"
                  >
                    Mark Absent
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose} data-testid="shift-cancel">Cancel</Button>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!selected || saving}
                    onClick={() => saveShift(selected)}
                    data-testid="shift-save"
                  >
                    Save shift
                  </Button>
                </div>
              </div>

              {absentOpen && (
                <div className="mt-3 border-2 border-amber-300 bg-amber-50 rounded-xl p-3" data-testid="absent-picker">
                  <div className="text-[11px] uppercase tracking-widest font-black text-amber-900 mb-2">Absent type</div>
                  <div className="grid grid-cols-1 gap-1.5">
                    {absentTypes.map((t) => (
                      <label key={t.code}
                        className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          absentType === t.code
                            ? "border-amber-500 bg-white"
                            : "border-slate-200 bg-white hover:bg-amber-100/40"
                        }`}
                        data-testid={`absent-type-${t.code}`}
                      >
                        <input type="radio" name="absent_type" value={t.code}
                          checked={absentType === t.code}
                          onChange={() => setAbsentType(t.code)}
                          className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                          <div className="text-[11px] text-slate-500 leading-snug">{t.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {absentType === "left_sick_early" && (
                    <div className="mt-2 rounded-lg border border-amber-400 bg-white p-2.5" data-testid="absent-left-at-panel">
                      <label className="text-[11px] font-semibold text-amber-900 flex items-center gap-1.5">
                        <Clock size={12} /> Time employee left (worked from {current?.start_time || "shift start"})
                      </label>
                      <input
                        type="time"
                        value={absentLeftAt}
                        onChange={(e) => setAbsentLeftAt(e.target.value)}
                        data-testid="absent-left-at-time"
                        className="mt-1.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm tabular-nums"
                      />
                      <div className="text-[10px] text-slate-500 mt-1">
                        We&apos;ll record hours worked from {current?.start_time || "start"} → this time. Cell keeps the original shift code but paints absent so it&apos;s obvious.
                      </div>
                    </div>
                  )}
                  <label className="block mt-2">
                    <span className="text-[11px] font-medium text-slate-700">Note (optional — manager only view)</span>
                    <input type="text" value={absentNote}
                      onChange={(e) => setAbsentNote(e.target.value)}
                      placeholder="Manager note…"
                      data-testid="absent-note"
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                  </label>
                  <div className="flex justify-end gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => setAbsentOpen(false)} data-testid="absent-cancel">Cancel</Button>
                    <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                      disabled={!absentType || saving}
                      onClick={submitAbsent}
                      data-testid="absent-submit"
                    >
                      {saving ? "Saving…" : "Mark absent"}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="notes" className="space-y-3 pt-3">
              <div className="space-y-2 max-h-64 overflow-y-auto thin-scroll pr-1">
                {notes.length === 0 && (
                  <div className="text-sm text-slate-500 text-center py-4">
                    No notes yet. Add one below.
                  </div>
                )}
                {notes.map((n) => {
                  const meta = VIS_META[n.visibility] || VIS_META.managers;
                  return (
                    <div key={n.id} className="rounded-lg border border-slate-200 p-3" data-testid={`note-${n.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-slate-700">{n.created_by_name}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500">
                            {mdyTime(n.created_at)}
                          </span>
                          <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${meta.badge}`}>
                            <meta.Icon size={10} /> {meta.label}
                          </span>
                        </div>
                        <button
                          onClick={() => deleteNote(n.id)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          data-testid={`note-delete-${n.id}`}
                          aria-label="Delete note"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.content}</div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5"><StickyNote size={14} /> New note</Label>
                  <div className="w-44">
                    <Select value={noteVis} onValueChange={setNoteVis}>
                      <SelectTrigger className="h-8 text-xs" data-testid="note-visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="managers">Managers only</SelectItem>
                        <SelectItem value="employee">This employee</SelectItem>
                        <SelectItem value="all">Everyone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add instructions, reminders, or context for this cell…"
                  className="min-h-[70px]"
                  data-testid="note-text"
                />
                <div className="flex justify-end">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={addNote} disabled={!noteText.trim()}
                    data-testid="note-add"
                  >
                    <Plus size={14} className="mr-1" /> Add note
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="history" className="pt-3">
              <div className="space-y-2 max-h-80 overflow-y-auto thin-scroll pr-1">
                {history.length === 0 && (
                  <div className="text-sm text-slate-500 text-center py-6">
                    No history yet for this cell.
                  </div>
                )}
                {history.map((h) => (
                  <div key={h.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`history-${h.id}`}>
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <span className="font-medium">{h.actor_name}</span>{" "}
                        <span className="text-slate-700">
                          {h.action === "assigned" && <>assigned shift <b>{h.new_code}</b></>}
                          {h.action === "changed"  && <>changed shift from <b>{h.prev_code}</b> to <b>{h.new_code}</b></>}
                          {h.action === "cleared"  && <>cleared shift (was <b>{h.prev_code}</b>)</>}
                          {h.action === "note_added"   && <>added a note ({h.note_visibility})</>}
                          {h.action === "note_deleted" && <>deleted a note</>}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 tabular-nums">
                        {mdyTime(h.created_at)}
                      </div>
                      {h.note_content && (
                        <div className="mt-1 text-xs italic text-slate-600 rounded bg-slate-50 border border-slate-200 p-2">
                          &ldquo;{h.note_content}&rdquo;
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="sm:justify-end">
            <Button variant="outline" onClick={onClose} data-testid="dialog-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
